import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Avatar,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requiresOTP, setRequiresOTP] = useState(false);
  const [sessionToken, setSessionToken] = useState('');
  const { login, verifyOTP, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/files" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!requiresOTP) {
        // Step 1: Initial login with username/password
        const result = await login({ username, password });

        if (result.requires_otp) {
          // OTP required - show OTP input field
          setRequiresOTP(true);
          setSessionToken(result.session_token || '');
        } else {
          // No OTP required - login complete
          navigate('/files');
        }
      } else {
        // Step 2: Verify OTP code
        await verifyOTP(sessionToken, otpCode);
        navigate('/files');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
        <Avatar sx={{ m: 1, bgcolor: 'success.main' }}>
          <Lock />
        </Avatar>
        <Typography component="h1" variant="h4" gutterBottom>
          MFT Edge Client
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Edge file transfer interface for external users
        </Typography>
        
        <Card sx={{ mt: 3, width: '100%', elevation: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography component="h2" variant="h5" align="center" gutterBottom>
              Sign In
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {requiresOTP && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please enter your 6-digit authentication code
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              {!requiresOTP ? (
                <>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="username"
                    label="Username"
                    name="username"
                    autoComplete="username"
                    autoFocus
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loading}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    disabled={loading || !username || !password}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Sign In'}
                  </Button>
                </>
              ) : (
                <>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="otp"
                    label="Authentication Code"
                    name="otp"
                    autoComplete="off"
                    autoFocus
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={loading}
                    placeholder="000000"
                    inputProps={{
                      maxLength: 6,
                      pattern: '[0-9]*',
                      inputMode: 'numeric',
                    }}
                    helperText="Enter the 6-digit code from your authenticator app"
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    disabled={loading || otpCode.length !== 6}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Verify'}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => {
                      setRequiresOTP(false);
                      setOtpCode('');
                      setSessionToken('');
                      setError('');
                    }}
                    disabled={loading}
                  >
                    Back to Login
                  </Button>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;